/**
 * Drag and drop for the tab strip (featureFlags.tabs), with @dnd-kit: reorder entries and groups, drop a tab onto
 * another (split view), onto a group chip (add to group) or onto a pane's edge (split that pane). Entries are plain
 * draggables + droppables; the drop point comes from the pointer's position over the hovered target, so the strip
 * shows an insertion line / a highlight instead of shuffling while dragging.
 */
import { Shell, type ShellState, type ShellViewModel, type SplitSide } from "@bundle/core";
import { useT } from "@bundle/ui";
import { DragDropProvider, DragOverlay, KeyboardSensor, useDraggable, useDroppable } from "@dnd-kit/react";
import { createContext, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { applyDrop, canCombine, sameIntent, stripItems, type DragSource, type DropIntent, type StripItem } from "./strip.ts";

type DropData =
  | { type: "item"; item: StripItem }
  | { type: "end" }
  | { type: "pane"; paneId: string; side: SplitSide };

type DragState = { source: DragSource | undefined; intent: DropIntent | undefined };

const DragContext = createContext<DragState>({ source: undefined, intent: undefined });

export function useTabDrag(): DragState {
  return useContext(DragContext);
}

function sourceOf(item: StripItem): DragSource {
  return item.kind === "unit" ? { kind: "unit", key: item.key, unit: item.unit } : { kind: "group", key: item.key, group: item.group };
}

type Operation = { target: { data: unknown; element?: Element } | null; position: { current: { x: number; y: number } } };

/** The split-tab segment (its tab id) under a point, within the strip entry `key`. */
function segmentAt(key: string, x: number, y: number): string | undefined {
  for (const el of document.elementsFromPoint(x, y)) {
    const seg = el.closest<HTMLElement>("[data-seg]");
    if (seg !== null && seg.closest<HTMLElement>("[data-strip-key]")?.dataset.stripKey === key) return seg.dataset.seg;
  }
  return undefined;
}

/** No collision yet (a quick drag released on its first move): the drop target under the pointer, from the DOM. */
function hitTest(state: ShellState, x: number, y: number): { data: DropData; element: Element } | undefined {
  for (const el of document.elementsFromPoint(x, y)) {
    const zone = el.closest("[data-pane-drop]");
    if (zone instanceof HTMLElement) return { element: zone, data: { type: "pane", paneId: zone.dataset.paneDrop!, side: zone.dataset.side as SplitSide } };
    if (el.closest("[data-strip-end]") !== null) return { element: el, data: { type: "end" } };
    const host = el.closest("[data-strip-key]");
    if (host instanceof HTMLElement) {
      const item = stripItems(state).find((it) => it.key === host.dataset.stripKey);
      if (item !== undefined) return { element: host, data: { type: "item", item } };
    }
  }
  return undefined;
}

/** The drop point for the pointer over the current target: halves before/after, the middle to combine / add to a group. */
function intentFor(state: ShellState, src: DragSource, op: Operation): DropIntent | undefined {
  const { x: px, y: py } = op.position.current;
  // A segment of a split tab over another segment of the same tab: swap sides (whatever dnd-kit's collision says).
  if (src.kind === "unit" && src.seg !== undefined) {
    const over = segmentAt(src.key, px, py);
    if (over !== undefined && over !== src.seg) return { kind: "swap", key: src.key, a: src.seg, b: over };
  }
  const hit = op.target === null ? hitTest(state, px, py) : { data: op.target.data as DropData | undefined, element: op.target.element };
  const data = hit?.data;
  if (hit === undefined || data === undefined) return undefined;
  if (data.type === "pane") return { kind: "pane", paneId: data.paneId, side: data.side };
  if (data.type === "end") return { kind: "end" };
  const el = hit.element;
  const item = data.item;
  if (el === undefined || item.key === src.key) return undefined;
  const r = el.getBoundingClientRect();
  const x = r.width === 0 ? 0.5 : (px - r.left) / r.width;
  if (item.kind === "unit" && canCombine(state, src, item.unit) && x > 0.3 && x < 0.7) return { kind: "combine", key: item.key };
  if (item.kind === "group" && src.kind === "unit" && !src.unit.pinned && x > 0.25 && x < 0.75) return { kind: "into", key: item.key };
  return { kind: x < 0.5 ? "before" : "after", key: item.key };
}

export function TabDndProvider({ shell, ghost, children }: { shell: ShellViewModel; ghost: (source: DragSource) => ReactNode; children: ReactNode }) {
  const [drag, setDrag] = useState<DragState>({ source: undefined, intent: undefined });
  const latest = useRef(drag);
  const set = (next: DragState) => {
    if (next.source === latest.current.source && sameIntent(next.intent, latest.current.intent)) return;
    latest.current = next;
    setDrag(next);
  };

  return (
    <DragDropProvider
      // Pointer only: the keyboard sensor would take Enter / Space / arrows on a focused tab (pick up and move it) from
      // selecting the tab and the strip's roving focus. Keyboard users group, split and close from the context menu.
      sensors={(defaults) => defaults.filter((sensor) => sensor !== KeyboardSensor)}
      onDragStart={(event) => {
        const data = event.operation.source?.data as { item?: StripItem } | undefined;
        let source = data?.item === undefined ? undefined : sourceOf(data.item);
        if (source?.kind === "unit" && source.unit.split) {
          // Started on one segment of a split tab: dropped on another segment, the two swap sides.
          const { x, y } = event.operation.position.initial;
          const seg = segmentAt(source.key, x, y);
          if (seg !== undefined) source = { ...source, seg };
        }
        set({ source, intent: undefined });
      }}
      onDragMove={(event) => {
        const src = latest.current.source;
        if (src !== undefined) set({ source: src, intent: intentFor(shell.state, src, event.operation) });
      }}
      onDragOver={(event) => {
        const src = latest.current.source;
        if (src !== undefined) set({ source: src, intent: intentFor(shell.state, src, event.operation) });
      }}
      onDragEnd={(event) => {
        const { source } = latest.current;
        // Where the pointer is released decides (a quick drag may end before dnd-kit has seen it move).
        const up = event.nativeEvent instanceof MouseEvent ? { target: null, position: { current: { x: event.nativeEvent.clientX, y: event.nativeEvent.clientY } } } : undefined;
        const intent = source === undefined ? undefined : ((up === undefined ? undefined : intentFor(shell.state, source, up)) ?? intentFor(shell.state, source, event.operation) ?? latest.current.intent);
        set({ source: undefined, intent: undefined });
        if (event.canceled || source === undefined || intent === undefined) return;
        const s = shell.state;
        applyDrop(shell, s, stripItems(s), source, intent);
      }}
    >
      <DragContext.Provider value={drag}>{children}</DragContext.Provider>
      <DragOverlay dropAnimation={null}>{() => (drag.source === undefined ? null : ghost(drag.source))}</DragOverlay>
    </DragDropProvider>
  );
}

/** Registers a strip item as draggable and as a drop target. */
export function useStripItemDnd(item: StripItem): { ref: (el: Element | null) => void; dragging: boolean; drop: DropIntent["kind"] | undefined } {
  const { source, intent } = useTabDrag();
  const data = useMemo(() => ({ type: "item" as const, item }), [item]);
  const { ref: dragRef, isDragSource } = useDraggable({ id: `d:${item.key}`, data: { item } });
  const dragging = isDragSource || source?.key === item.key || (source?.kind === "group" && item.kind === "unit" && item.group?.id === source.group.id);
  const { ref: dropRef } = useDroppable({ id: `o:${item.key}`, data, disabled: dragging });
  const ref = useMemo(
    () => (el: Element | null) => {
      dragRef(el);
      dropRef(el);
    },
    [dragRef, dropRef],
  );
  const drop = intent !== undefined && intent.kind !== "end" && intent.kind !== "pane" && intent.kind !== "swap" && intent.key === item.key ? intent.kind : undefined;
  return { ref, dragging, drop };
}

/** The empty strip after the last entry: drop there to move to the end (out of any group). */
export function StripEnd() {
  const { source, intent } = useTabDrag();
  const { ref } = useDroppable({ id: "o:end", data: { type: "end" } satisfies DropData });
  return <div ref={ref} className={["tab-strip-end", source !== undefined && intent?.kind === "end" && "tab-strip-end-over"].filter(Boolean).join(" ")} data-strip-end aria-hidden />;
}

const SIDES: SplitSide[] = ["left", "right", "top", "bottom"];

function EdgeZone({ paneId, side }: { paneId: string; side: SplitSide }) {
  const { ref } = useDroppable({ id: `pane:${paneId}:${side}`, data: { type: "pane", paneId, side } satisfies DropData });
  return <div ref={ref} className={`pane-drop-zone pane-drop-zone-${side}`} data-pane-drop={paneId} data-side={side} data-testid={`pane-drop-${side}`} />;
}

/**
 * Over a pane while a tab is dragged: edge zones that split this pane, and a preview of where the new pane goes. Not on
 * the pane already showing the dragged tab alone, nor when the layout is full.
 */
export function PaneDropZones({ paneId, tabId, shell }: { paneId: string; tabId: string; shell: ShellViewModel }) {
  const t = useT();
  const { source, intent } = useTabDrag();
  if (source === undefined || source.kind !== "unit" || source.unit.split || source.unit.tabIds[0] === tabId) return null;
  if (Shell.panes(shell.state.layout).length >= shell.state.maxPanes) return null;
  const over = intent?.kind === "pane" && intent.paneId === paneId ? intent.side : undefined;
  return (
    <div className="pane-drop" data-testid={`pane-drop-${paneId}`}>
      {over === undefined ? null : (
        <div className={`pane-drop-preview pane-drop-preview-${over}`}>
          <span className="pane-drop-hint">{t("shell.split.dropHint")}</span>
        </div>
      )}
      {SIDES.map((side) => (
        <EdgeZone key={side} paneId={paneId} side={side} />
      ))}
    </div>
  );
}
